/**
 * Kavacha: chat links and quiet footer labels.
 *
 * 1. A link whose address ends in `#chat` or `#fastest-help` opens Shopify Inbox chat in place,
 *    through the `show()` method of the `<shopify-chat>` element the Inbox app embed adds. Inbox
 *    documents no API for this; `show()` is the element's own public method (checked on the live
 *    store, 2026-09-24). When the element or the method is missing, or the shopper asks for a new
 *    tab or window, the link goes where it points (the Contact page's Fastest help section).
 *    Focus then moves into the chat panel, as it does from the stock launcher.
 * 2. Footer menu headings are labels, not controls: while the menu's accordion is disabled at the
 *    current width, its `<summary>` leaves the tab order and is exposed as a level 2 heading
 *    (Heritage blocks the toggle, so the heading role is honest).
 * 3. The launcher's height and inset vary (20 px on an iPhone, 88 px on a short 320 x 700 viewport,
 *    measured 2026-09-24), so `--kavacha-chat-clearance` on the root is measured from the launcher
 *    itself: the distance from its top edge to the viewport bottom, plus 12 px. The footer's legal row
 *    keeps that much room at its end (snippets/kavacha-footer.liquid).
 */

const CHAT_HASHES = new Set(['#chat', '#fastest-help']);

/** @param {MouseEvent} event */
function openChatFromLink(event) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const link = target?.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;

  let url;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin || !CHAT_HASHES.has(url.hash)) return;

  const chat = /** @type {(HTMLElement & { show?: () => unknown }) | null} */ (
    document.querySelector('shopify-chat')
  );
  if (!chat || typeof chat.show !== 'function') return;

  event.preventDefault();
  const fallBack = () => window.location.assign(link.href);
  try {
    // show() is synchronous today; the Promise wrap keeps the fallback if a later Inbox makes it async.
    // A failed show() goes to the Contact link; a failed focus() leaves the open chat as it is.
    Promise.resolve(chat.show()).then(() => focusChat(chat, link), fallBack);
  } catch {
    fallBack();
  }
}

/**
 * On a page's first open the panel is built after show() returns, and focus() before then does
 * nothing, so it retries for up to 1.5 s while focus is still on the link (never pulling focus the
 * shopper has moved).
 * @param {HTMLElement} chat
 * @param {HTMLAnchorElement} link
 * @param {number} [tries]
 */
function focusChat(chat, link, tries = 0) {
  const active = document.activeElement;
  if (active === chat || (active !== link && active !== document.body)) return;
  try {
    chat.focus();
  } catch {
    return;
  }
  if (document.activeElement !== chat && tries < 10) {
    setTimeout(() => focusChat(chat, link, tries + 1), 150);
  }
}

document.addEventListener('click', openChatFromLink);

const desktop = window.matchMedia('(min-width: 750px)');

function quietFooterLabels() {
  for (const accordion of document.querySelectorAll('.footer-content accordion-custom')) {
    if (!(accordion instanceof HTMLElement)) continue;
    const disabled = desktop.matches
      ? accordion.dataset.disableOnDesktop === 'true'
      : accordion.dataset.disableOnMobile === 'true';
    const summary = accordion.querySelector('summary');
    if (!summary) continue;
    if (disabled) {
      summary.setAttribute('tabindex', '-1');
      summary.setAttribute('role', 'heading');
      summary.setAttribute('aria-level', '2');
    } else {
      summary.removeAttribute('tabindex');
      summary.removeAttribute('role');
      summary.removeAttribute('aria-level');
    }
  }
}

quietFooterLabels();
desktop.addEventListener('change', quietFooterLabels);
document.addEventListener('shopify:section:load', quietFooterLabels);

/**
 * @param {ShadowRoot | null | undefined} root
 * @returns {Element | null}
 */
function findActivator(root) {
  if (!root) return null;
  const activator = root.querySelector('[part~="activator"]');
  if (activator) return activator;
  for (const element of root.querySelectorAll('*')) {
    const found = findActivator(element.shadowRoot);
    if (found) return found;
  }
  return null;
}

function measureChatClearance() {
  const activator = findActivator(document.querySelector('shopify-chat')?.shadowRoot);
  if (!activator) return false;
  const rect = activator.getBoundingClientRect();
  if (!rect.height) return false;
  const clearance = Math.max(0, Math.round(window.innerHeight - rect.top + 12));
  document.documentElement.style.setProperty('--kavacha-chat-clearance', `${clearance}px`);
  return true;
}

if (window.customElements) {
  customElements.whenDefined('shopify-chat').then(() => {
    let tries = 0;
    const poll = () => {
      if (measureChatClearance() || ++tries > 20) return;
      setTimeout(poll, 250);
    };
    poll();
  });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measureChatClearance, 200);
  });
}
